import { useRef, useState } from 'react';
import { getAuth, deleteUser } from 'firebase/auth';
import { toast } from 'sonner';
import { Download, Upload, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { exportAll, planRestore, applyRestore, deleteAllData, validateBackup, type BackupFile, type RestorePlan } from '../../../ledger/backup';

const uid = () => getAuth().currentUser!.uid;
const KEY = 'v2-last-backup';
const lastBackup = () => { try { return localStorage.getItem(KEY); } catch { return null; } };
const CONFIRM = 'XOÁ TOÀN BỘ';

export default function DataTab() {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(lastBackup());
  const [backedUp, setBackedUp] = useState(false);
  const [file, setFile] = useState<BackupFile | null>(null);
  const [plan, setPlan] = useState<RestorePlan | null>(null);
  const [restoreMsg, setRestoreMsg] = useState<string | null>(null);
  const [phrase, setPhrase] = useState('');
  const [alsoAccount, setAlsoAccount] = useState(false);
  const [wipeMsg, setWipeMsg] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const backup = async () => {
    setBusy(true);
    try {
      const f = await exportAll(uid());
      const blob = new Blob([JSON.stringify(f)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `fintrack-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      const n = Object.values(f.collections).reduce((s, r) => s + r.length, 0);
      const stamp = new Date().toLocaleString('vi-VN');
      try { localStorage.setItem(KEY, stamp); } catch { /* bỏ qua */ }
      setLast(stamp); setBackedUp(true); toast.success(`Đã tải bản sao lưu (${n} bản ghi)`);
    } catch (e) { toast.error(`Không sao lưu được: ${(e as Error).message}`); }
    setBusy(false);
  };

  const pick = async (f?: File) => {
    setPlan(null); setFile(null); setRestoreMsg(null);
    if (!f) return;
    try {
      const parsed = JSON.parse(await f.text());
      const err = validateBackup(parsed);
      if (err) { setRestoreMsg(err); return; }
      setBusy(true);
      setPlan(await planRestore(uid(), parsed)); setFile(parsed);
    } catch { setRestoreMsg('Không đọc được file — chọn đúng file .json do FinTrack sao lưu.'); }
    setBusy(false);
  };
  const restore = async () => {
    if (!file) return;
    setBusy(true);
    const r = await applyRestore(uid(), file);
    setBusy(false);
    setRestoreMsg(r.failures.length === 0 ? `Đã khôi phục ${r.written} bản ghi còn thiếu.` : `Đã khôi phục ${r.written} bản ghi; ${r.failures.length} lô bị từ chối (${r.failures[0].key}: ${r.failures[0].error}).`);
    setFile(null); setPlan(null); if (input.current) input.current.value = '';
  };

  const wipe = async () => {
    setBusy(true); setWipeMsg(null);
    const r = await deleteAllData(uid());
    let msg = r.failures.length ? `Đã xoá ${r.deleted} bản ghi, nhưng ${r.failures.length} lô lỗi: ${r.failures[0]}` : `Đã xoá ${r.deleted} bản ghi.`;
    if (alsoAccount && r.failures.length === 0) {
      try { await deleteUser(getAuth().currentUser!); msg += ' Tài khoản đăng nhập đã bị xoá.'; }
      catch (e) { msg += (e as { code?: string }).code === 'auth/requires-recent-login' ? ' Chưa xoá được tài khoản đăng nhập vì phiên đã cũ — đăng xuất, đăng nhập lại rồi thử lại.' : ` Không xoá được tài khoản: ${(e as Error).message}`; }
    }
    setWipeMsg(msg); setPhrase(''); setBusy(false);
  };

  return (
    <div className="space-y-6">
      <Card><CardContent className="space-y-3 p-4">
        <h3 className="font-medium">Sao lưu</h3>
        <p className="max-w-prose text-sm text-muted-foreground">Tải toàn bộ dữ liệu (ví, giao dịch, nợ, ngân sách, mục tiêu, lịch sử…) thành một file JSON lưu trên máy bạn. Nên sao lưu định kỳ và trước mọi thay đổi lớn.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={backup} disabled={busy}><Download size={15} aria-hidden /> Tải bản sao lưu</Button>
          <span className="text-xs text-muted-foreground">{last ? `Lần gần nhất trên thiết bị này: ${last}` : 'Chưa sao lưu từ thiết bị này.'}</span>
        </div>
      </CardContent></Card>

      <Card><CardContent className="space-y-3 p-4">
        <h3 className="font-medium">Khôi phục</h3>
        <p className="max-w-prose text-sm text-muted-foreground">Chọn file sao lưu. Chỉ các bản ghi <b>còn thiếu</b> được tạo lại; bản ghi đang có <b>không bao giờ bị ghi đè hay xoá</b>.</p>
        <Input ref={input} type="file" accept="application/json,.json" onChange={e => pick(e.target.files?.[0])} className="max-w-sm" aria-label="Chọn file sao lưu" disabled={busy} />
        {plan && (
          <div className="space-y-2">
            <p className="text-sm">{plan.totalMissing === 0 ? 'Mọi bản ghi trong file đã có — không cần khôi phục gì.' : <>Sẽ tạo lại <b>{plan.totalMissing}</b> bản ghi còn thiếu:</>}</p>
            {plan.totalMissing > 0 && <ul className="num grid gap-x-6 text-xs text-muted-foreground sm:grid-cols-2">{plan.rows.filter(r => r.missing > 0).map(r => <li key={r.key} className="flex justify-between"><span>{r.key}</span><span>{r.missing}/{r.total}</span></li>)}</ul>}
            {plan.totalMissing > 0 && <Button onClick={restore} disabled={busy}><Upload size={15} aria-hidden /> Khôi phục {plan.totalMissing} bản ghi</Button>}
          </div>
        )}
        {restoreMsg && <p role="status" className="rounded-md bg-muted px-3 py-2 text-sm">{restoreMsg}</p>}
      </CardContent></Card>

      <Card className="border-bad/40"><CardContent className="space-y-3 p-4">
        <h3 className="flex items-center gap-2 font-medium text-bad"><AlertTriangle size={16} aria-hidden /> Xoá toàn bộ dữ liệu</h3>
        <p className="max-w-prose text-sm text-muted-foreground">Xoá vĩnh viễn mọi dữ liệu tài chính của bạn trên máy chủ. <b>Không thể hoàn tác</b> — trừ khi bạn đã có file sao lưu.</p>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={backedUp} onChange={e => setBackedUp(e.target.checked)} />
          <span>Tôi đã tải bản sao lưu và biết nơi cất file.</span></label>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={alsoAccount} onChange={e => setAlsoAccount(e.target.checked)} />
          <span>Xoá luôn tài khoản đăng nhập của ứng dụng này.</span></label>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={phrase} onChange={e => setPhrase(e.target.value)} placeholder={`Gõ "${CONFIRM}" để xác nhận`} className="max-w-64" aria-label="Gõ cụm xác nhận" />
          <Button variant="destructive" onClick={wipe} disabled={busy || !backedUp || phrase !== CONFIRM}>Xoá dữ liệu</Button>
        </div>
        {wipeMsg && <p role="status" className="rounded-md bg-muted px-3 py-2 text-sm">{wipeMsg}</p>}
      </CardContent></Card>
    </div>
  );
}
